import type { Container } from 'pixi.js';
import { EnemyState, EnemyType, POOL_CAPACITY } from '../../../constants';
import type { GameWorld } from '../../../world/GameWorld';
import type { LayerStackSprite } from '../../layerstack/LayerStackSprite';
import type { MobilePoolSnapshot } from '../MobilePoolObserver';
import { ENEMY_SLUGS } from '../assets/registry';
import type { StackRegistry } from '../assets/StackRegistry';
import { placeSprite } from '../scale';
import type { EntityFamily, FamilyDeps, FrameContext } from './types';

/** Rendered frames a hit flash is held for. */
const HIT_FRAMES = 6;
const STATE_DYING = 'dying';
const STATE_HIT = 'hit';
const STATE_NEUTRAL = 'neutral';

/** Authored state per EnemyState, by index. */
const ENEMY_STATES: readonly string[] = Object.freeze([
  'neutral', // Free — never drawn
  'enter', // Spawn
  'enter', // Enter
  'neutral', // Navigate
  'neutral', // Aim
  'telegraph', // Telegraph
  'attack', // Attack
  'neutral', // Recover
  'neutral', // Disabled
  'dying', // Dying
]);

/**
 * One sprite slot per enemy pool slot, but the slug behind a slot changes with
 * the occupant, so this family owns both the slot→slug binding and the hit
 * flash bookkeeping (prev hp per slot) that must reset when the binding does.
 */
export class EnemiesFamily implements EntityFamily {
  private stacks: StackRegistry | null = null;
  private layer: Container | null = null;
  private mobilePerformanceMode = false;
  private mobilePool: MobilePoolSnapshot | null = null;
  private readonly sprites: (LayerStackSprite | null)[] = [];
  private readonly slugs: string[] = [];
  private readonly prevHp = new Float32Array(POOL_CAPACITY.enemies);
  private readonly hitFrames = new Uint8Array(POOL_CAPACITY.enemies);

  init(deps: FamilyDeps): void {
    this.stacks = deps.stacks;
    this.layer = deps.layer;
    this.mobilePerformanceMode = deps.mobilePerformanceMode;
    this.mobilePool = deps.mobilePools?.enemies ?? null;
    for (let i = 0; i < POOL_CAPACITY.enemies; i += 1) {
      this.sprites.push(null);
      this.slugs.push('');
    }
  }

  sync(world: GameWorld, _frame: FrameContext): void {
    if (this.mobilePerformanceMode) {
      this.syncMobile(world);
      return;
    }
    const pool = world.enemies;
    for (let i = 0; i < this.sprites.length; i += 1) {
      this.syncSlot(world, i, pool.indices.active[i] !== 0);
    }
  }

  dispose(): void {
    this.sprites.length = 0;
    this.slugs.length = 0;
    this.mobilePool = null;
    this.mobilePerformanceMode = false;
    this.layer = null;
    this.stacks = null;
  }

  private syncMobile(world: GameWorld): void {
    const observed = this.mobilePool;
    if (!observed) return;
    let activeIndex = 0;
    let deactivatedIndex = 0;
    while (activeIndex < observed.activeCount || deactivatedIndex < observed.deactivatedCount) {
      const activeSlot = activeIndex < observed.activeCount
        ? observed.activeSlots[activeIndex]!
        : POOL_CAPACITY.enemies;
      const deactivatedSlot = deactivatedIndex < observed.deactivatedCount
        ? observed.deactivatedSlots[deactivatedIndex]!
        : POOL_CAPACITY.enemies;
      // Equal slots only occur on a scene reset. Release the old proxy first,
      // then let the next iteration bind the new scene's occupant.
      if (deactivatedSlot <= activeSlot) {
        this.syncSlot(world, deactivatedSlot, false);
        deactivatedIndex += 1;
        continue;
      }
      this.syncSlot(world, activeSlot, true);
      activeIndex += 1;
    }
  }

  private syncSlot(world: GameWorld, i: number, active: boolean): void {
    const stacks = this.stacks;
    if (!stacks) return;
    const pool = world.enemies;
    const slug = this.slugs[i]!;
    if (!active) {
      const idle = this.sprites[i];
      if (idle) {
        stacks.release(slug, idle);
        this.sprites[i] = null;
        this.slugs[i] = '';
      }
      return;
    }
    const wanted = ENEMY_SLUGS[pool.type[i]!] ?? ENEMY_SLUGS[EnemyType.Threadling]!;
    let sprite = this.sprites[i];
    if (!sprite || slug !== wanted) {
      // A slot is reused across respawns; every carried-over value resets
      // here, or the new occupant inherits the old one's hp and flashes.
      if (sprite) stacks.release(slug, sprite);
      sprite = stacks.acquire(wanted, this.layer);
      this.sprites[i] = sprite;
      this.slugs[i] = wanted;
      this.prevHp[i] = pool.hp[i]!;
      this.hitFrames[i] = 0;
    }
    const enemyState = pool.state[i]!;
    const dying = enemyState === EnemyState.Dying;
    if (pool.hp[i]! < this.prevHp[i]! && !dying) this.hitFrames[i] = HIT_FRAMES;
    else if (this.hitFrames[i]! > 0) this.hitFrames[i] -= 1;
    this.prevHp[i] = pool.hp[i]!;
    const state = dying ? STATE_DYING : this.hitFrames[i]! > 0 ? STATE_HIT : ENEMY_STATES[enemyState] ?? STATE_NEUTRAL;
    stacks.applyState(wanted, sprite, state);
    sprite.setMirror(pool.vx[i]! > 0);
    placeSprite(sprite, pool.x[i]!, pool.y[i]!);
  }
}
